import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import InfiniteMenu from './InfiniteMenu';
import {
  Code,
  Smartphone,
  Database,
  GitBranch,
  Palette,
  Monitor,
  Server,
  Globe
} from 'lucide-react';

const Skills = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  // Fonction pour générer une couleur basée sur le nom
  const generateColorFromName = (name) => {
    const colors = [
      '#3B82F6', // blue
      '#10B981', // green
      '#F59E0B', // yellow
      '#EF4444', // red
      '#8B5CF6', // purple
      '#06B6D4', // cyan
      '#F97316', // orange
      '#EC4899', // pink
      '#84CC16', // lime
      '#6366F1', // indigo
    ];
    const hash = name.split('').reduce((a, b) => {
      a = ((a << 5) - a) + b.charCodeAt(0);
      return a & a;
    }, 0);
    return colors[Math.abs(hash) % colors.length];
  };

  // Fonction pour créer une image SVG colorée
  const createColoredSVG = (name, color) => {
    // Déterminer la couleur du texte (blanc ou noir selon la luminosité du fond)
    const hex = color.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    const textColor = luminance > 0.5 ? '#000000' : '#FFFFFF';
    
    // Ajuster la taille de police selon la longueur du texte
    let fontSize = 24;
    if (name.length > 10) fontSize = 20;
    if (name.length > 15) fontSize = 16;
    
    const svg = `
      <svg width="300" height="300" xmlns="http://www.w3.org/2000/svg">
        <rect width="300" height="300" fill="${color}"/>
        <text x="150" y="150" font-family="Arial, sans-serif" font-size="${fontSize}" 
              fill="${textColor}" text-anchor="middle" dominant-baseline="middle" font-weight="bold">
          ${name}
        </text>
      </svg>
    `;
    return `data:image/svg+xml;base64,${btoa(svg)}`;
  };

  const skillCategories = [
    {
      title: 'Développement Frontend',
      icon: Code,
      color: 'primary',
      skills: [
        { name: 'HTML', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/html5/html5-original.svg' },
        { name: 'CSS', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/css3/css3-original.svg' },
        { name: 'JavaScript', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/javascript/javascript-original.svg' },
        { name: 'Bootstrap', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/bootstrap/bootstrap-original.svg' },
        { name: 'Tailwind CSS', logo: 'data:image/svg+xml;base64,' + btoa(`
          <svg width="300" height="300" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <linearGradient id="tailwindGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" style="stop-color:#06B6D4;stop-opacity:1" />
                <stop offset="100%" style="stop-color:#0891B2;stop-opacity:1" />
              </linearGradient>
            </defs>
            <rect width="300" height="300" fill="url(#tailwindGradient)"/>
            <text x="150" y="130" font-family="Arial, sans-serif" font-size="22" fill="white" text-anchor="middle" font-weight="bold">Tailwind</text>
            <text x="150" y="160" font-family="Arial, sans-serif" font-size="22" fill="white" text-anchor="middle" font-weight="bold">CSS</text>
            <circle cx="150" cy="190" r="8" fill="white" opacity="0.8"/>
          </svg>
        `) },
        { name: 'React.js', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/react/react-original.svg' },
      ]
    },
    {
      title: 'Développement Mobile',
      icon: Smartphone,
      color: 'secondary',
      skills: [
        { name: 'React Native', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/react/react-original.svg' },
      ]
    },
    {
      title: 'Développement Backend',
      icon: Server,
      color: 'primary',
      skills: [
        { name: 'Node.js', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nodejs/nodejs-original.svg' },
        { name: 'Express.js', logo: 'data:image/svg+xml;base64,' + btoa(`
          <svg width="300" height="300" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <linearGradient id="expressGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" style="stop-color:#000000;stop-opacity:1" />
                <stop offset="100%" style="stop-color:#333333;stop-opacity:1" />
              </linearGradient>
            </defs>
            <rect width="300" height="300" fill="url(#expressGradient)"/>
            <text x="150" y="140" font-family="Arial, sans-serif" font-size="24" fill="white" text-anchor="middle" font-weight="bold">Express</text>
            <text x="150" y="170" font-family="Arial, sans-serif" font-size="24" fill="white" text-anchor="middle" font-weight="bold">.js</text>
            <path d="M120 190 L150 210 L180 190" stroke="white" stroke-width="2" fill="none"/>
            <circle cx="150" cy="220" r="3" fill="white"/>
          </svg>
        `) },
        { name: 'Java', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/java/java-original.svg' },
        { name: 'PHP', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/php/php-original.svg' },
        { name: 'Laravel', logo: 'data:image/svg+xml;base64,' + btoa(`
          <svg width="300" height="300" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <linearGradient id="laravelGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" style="stop-color:#FF2D20;stop-opacity:1" />
                <stop offset="100%" style="stop-color:#E53E3E;stop-opacity:1" />
              </linearGradient>
            </defs>
            <rect width="300" height="300" fill="url(#laravelGradient)"/>
            <text x="150" y="140" font-family="Arial, sans-serif" font-size="26" fill="white" text-anchor="middle" font-weight="bold">Laravel</text>
            <path d="M120 170 L150 190 L180 170" stroke="white" stroke-width="3" fill="none"/>
            <circle cx="150" cy="200" r="4" fill="white"/>
          </svg>
        `) },
      ]
    },
    {
      title: 'Gestion de bases de données',
      icon: Database,
      color: 'secondary',
      skills: [
        { name: 'MySQL', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/mysql/mysql-original.svg' },
        { name: 'MongoDB', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/mongodb/mongodb-original.svg' },
      ]
    },
    {
      title: 'Gestion de projet',
      icon: GitBranch,
      color: 'primary',
      skills: [
        { name: 'Git', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/git/git-original.svg' },
        { name: 'GitHub', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/github/github-original.svg' },
        { name: 'GitLab', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/gitlab/gitlab-original.svg' },
      ]
    },
    {
      title: 'Conception & Modélisation',
      icon: Palette,
      color: 'secondary',
      skills: [
        { name: 'UML', logo: 'data:image/svg+xml;base64,' + btoa(`
          <svg width="300" height="300" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <linearGradient id="umlGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" style="stop-color:#E91E63;stop-opacity:1" />
                <stop offset="100%" style="stop-color:#C2185B;stop-opacity:1" />
              </linearGradient>
            </defs>
            <rect width="300" height="300" fill="url(#umlGradient)"/>
            <text x="150" y="140" font-family="Arial, sans-serif" font-size="30" fill="white" text-anchor="middle" font-weight="bold">UML</text>
            <rect x="110" y="160" width="80" height="40" fill="none" stroke="white" stroke-width="2"/>
            <rect x="110" y="210" width="80" height="40" fill="none" stroke="white" stroke-width="2"/>
            <line x1="150" y1="200" x2="150" y2="210" stroke="white" stroke-width="2"/>
          </svg>
        `) },
        { name: 'Figma', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/figma/figma-original.svg' },
      ]
    },
  ];

  const getColorClasses = (color) => {
    const colors = {
      primary: {
        bg: 'bg-primary-100 dark:bg-primary-900',
        text: 'text-primary-800 dark:text-primary-200',
        border: 'border-primary-200 dark:border-primary-700',
        icon: 'text-primary-600',
      },
      secondary: {
        bg: 'bg-secondary-100 dark:bg-secondary-900',
        text: 'text-secondary-800 dark:text-secondary-200',
        border: 'border-secondary-200 dark:border-secondary-700',
        icon: 'text-secondary-600',
      },
    };
    return colors[color];
  };

  return (
    <section id="skills" className="section-padding bg-gray-50 dark:bg-dark-900">
      <div className="container-custom">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
            Outils & <span className="gradient-text">Technologies</span>
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
            Un aperçu des technologies et outils que je maîtrise pour créer des solutions innovantes.
          </p>
        </motion.div>

        <div style={{ height: '600px', position: 'relative' }}>
          <InfiniteMenu items={(() => {
            // Créer une liste unique de tous les outils sans répétition
            const allSkills = [];
            const seenSkills = new Set();
            
            skillCategories.forEach(category => {
              category.skills.forEach(skill => {
                if (!seenSkills.has(skill.name)) {
                  seenSkills.add(skill.name);
                  allSkills.push({
                    image: skill.logo,
                    link: '#',
                    title: skill.name,
                    description: `Utilisé dans ${skillCategories.filter(cat => 
                      cat.skills.some(s => s.name === skill.name)
                    ).map(cat => cat.title).join(', ')}`
                  });
                }
              });
            });
            
            return allSkills;
          })()} />
        </div>

        {/* Compétences transversales */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="mt-16"
        >
          <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">
            Compétences transversales
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              {
                title: 'Résolution de problèmes',
                icon: Code,
                description: 'Analyse et résolution efficace de problèmes complexes'
              },
              {
                title: 'Travail en équipe',
                icon: Globe,
                description: 'Collaboration efficace dans des projets multidisciplinaires'
              },
              {
                title: 'Apprentissage continu',
                icon: Monitor,
                description: 'Veille technologique et formation continue'
              },
              {
                title: 'Gestion de projet',
                icon: GitBranch,
                description: 'Planification et suivi de projets informatiques'
              }
            ].map((skill, index) => (
              <motion.div
                key={skill.title}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={inView ? { opacity: 1, scale: 1 } : {}}
                transition={{ duration: 0.5, delay: 0.7 + index * 0.1 }}
                className="text-center p-4 rounded-xl bg-gray-50 dark:bg-dark-700 hover:bg-gray-100 dark:hover:bg-dark-600 transition-colors"
              >
                <div className="w-12 h-12 mx-auto mb-3 rounded-lg bg-primary-100 dark:bg-primary-900 flex items-center justify-center">
                  <skill.icon size={24} className="text-primary-600" />
                </div>
                <h4 className="font-semibold text-gray-900 dark:text-white mb-2">
                  {skill.title}
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {skill.description}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Skills;